"use client";

import React, {
  useRef,
  useState,
  useCallback,
  useId,
} from "react";
import Image from "next/image";

export interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel: string;
  afterLabel: string;
  beforeAlt: string;
  afterAlt: string;
  /** Initial split position 0–100, defaults to 65 */
  initialPosition?: number;
  /** Eagerly load the after (Power BI) image — set when this is the LCP element */
  priority?: boolean;
}

export default function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel,
  afterLabel,
  beforeAlt,
  afterAlt,
  initialPosition = 65,
  priority = false,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
  const sliderId = useId();

  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const rawPos = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(Math.max(rawPos, 5), 100));
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
      updatePosition(e.clientX);
    },
    [updatePosition],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      updatePosition(e.clientX);
    },
    [isDragging, updatePosition],
  );

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPosition((p) => Math.max(p - 5, 5));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setPosition((p) => Math.min(p + 5, 100));
    }
  }, []);

  // If user prefers reduced motion, render static split layout
  if (prefersReducedMotion) {
    return (
      <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden w-full aspect-[16/9]">
        <div className="relative overflow-hidden">
          <Image
            src={afterImage}
            alt={afterAlt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 50vw, 400px"
            priority={priority}
          />
          <span className="absolute bottom-3 left-3 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-md">
            {afterLabel}
          </span>
        </div>
        <div className="relative overflow-hidden">
          <Image
            src={beforeImage}
            alt={beforeAlt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 50vw, 400px"
          />
          <span className="absolute bottom-3 right-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md">
            {beforeLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative select-none rounded-xl overflow-hidden w-full aspect-[16/9] cursor-col-resize"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      aria-label={`Vergleich: ${beforeLabel} vs. ${afterLabel}`}
    >
      {/* After image (full background — visible on the left) */}
      <div className="absolute inset-0">
        <Image
          src={afterImage}
          alt={afterAlt}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 600px"
          draggable={false}
          priority={priority}
        />
        <span className="absolute bottom-3 left-3 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-md z-10">
          {afterLabel}
        </span>
      </div>

      {/* Before image (clipped from the left — visible on the right) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        aria-hidden="true"
      >
        <Image
          src={beforeImage}
          alt={beforeAlt}
          fill
          className="object-cover grayscale-[30%] brightness-75"
          sizes="(max-width: 768px) 100vw, 600px"
          draggable={false}
        />
        {/* Aged overlay */}
        <div className="absolute inset-0 bg-amber-950/30 mix-blend-multiply" />
        <span className="absolute bottom-3 right-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md">
          {beforeLabel}
        </span>
      </div>

      {/* Divider line */}
      <div
        className="absolute inset-y-0 w-px bg-white shadow-[0_0_8px_rgba(0,0,0,0.6)] pointer-events-none"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      />

      {/* Drag handle */}
      <div
        role="slider"
        id={sliderId}
        tabIndex={0}
        aria-label={`Vergleichs-Regler: ${beforeLabel} vs. ${afterLabel}`}
        aria-valuemin={5}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        onKeyDown={onKeyDown}
        className="absolute inset-y-0 flex items-center justify-center pointer-events-none"
        style={{ left: `${position}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-gray-200 pointer-events-auto">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 4L2 9L6 14M12 4L16 9L12 14"
              stroke="#1e3a5f"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
